import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Shared purchase behaviour for the sample PDP (<aromely-sample-mvp>) and the gift-set PDP
 * (<aromely-bundle-mvp>). Both add real variants through Theme.routes.cart_add_url and open
 * the existing cart drawer with the theme's CartAddEvent — no invented SKUs, no discounts.
 */
class AromelyKitBase extends Component {
  #ac = new AbortController();
  #sticky = null;
  #stickyIO = null;
  #drawerMO = null;
  qty = 1;
  busy = false;

  connectedCallback() {
    super.connectedCallback();
    const section = this.closest('[data-akm-root]');
    section?.querySelectorAll('[data-akm-jump]').forEach((a) =>
      a.addEventListener('click', (e) => {
        e.preventDefault();
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, { signal: this.#ac.signal })
    );
    this.setupSticky(section);
    this.render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#ac.abort();
    this.#stickyIO?.disconnect();
    this.#drawerMO?.disconnect();
    this.setStickyVisible(false);
  }

  get signal() { return this.#ac.signal; }

  money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '${{amount}}', this.dataset.currency || 'USD');
  }

  qtyUp() { if (this.qty < 10) { this.qty += 1; this.render(); } }
  qtyDown() { if (this.qty > 1) { this.qty -= 1; this.render(); } }

  /** October Sale percentage while the promotion is live in the browser (from the promo state), else 0. */
  get promoPct() {
    const promo = window.AromelyPromo;
    if (!promo || typeof promo.active !== 'function' || !promo.active()) return 0;
    let pct = Number(promo.state?.pct);
    if (!Number.isFinite(pct) || pct <= 0) {
      try { pct = Number(JSON.parse(document.getElementById('aromely-promo-state')?.textContent || '{}').pct); } catch { pct = 0; }
    }
    return Number.isFinite(pct) && pct > 0 ? pct : 0;
  }
  /** Sale price of one unit in cents; Shopify truncates the OCTSALE discount per unit. */
  saleCents(cents) {
    const pct = this.promoPct;
    return pct > 0 ? cents - Math.floor((cents * pct) / 100) : cents;
  }
  /** Repaint a shared price line: sale price, struck full price and the sale badge (mirrors aromely-diffuser-mvp). */
  paintSaleLine(priceEl, compareEl, badgeEl, fullCents, suffix = '') {
    const pct = this.promoPct;
    const sale = this.saleCents(fullCents);
    const show = pct > 0 && fullCents > sale;
    if (priceEl) priceEl.textContent = `${this.money(sale)}${suffix}`;
    if (compareEl) { compareEl.hidden = !show; compareEl.textContent = show ? this.money(fullCents) : ''; }
    if (badgeEl) {
      const wrap = priceEl?.closest('[data-promo-label]');
      const prefix = wrap?.dataset.promoLabel || `${window.AromelyPromo?.state?.name || 'Sale'} · −`;
      badgeEl.textContent = show ? `${prefix}${pct}%` : '';
      badgeEl.classList.toggle('hidden', !show);
      badgeEl.classList.toggle('inline-flex', show);
    }
    return sale;
  }

  /** The live cart (count + JSON), or null when it cannot be read. */
  async fetchCart() {
    try {
      return await fetch(`${Theme.routes.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } }).then((x) => x.json());
    } catch { return null; }
  }

  /** The cart count the header shows right now (NaN when there is no badge to read). */
  badgeCount() {
    return parseInt(document.querySelector('[ref="cartItemCount"]')?.textContent || '', 10);
  }

  /**
   * After a failed or unconfirmed request, say what really happened: compare the live cart with the
   * count we started from instead of assuming nothing was added (Shopify can add part of a request,
   * and a dropped connection can hide a success). Refreshes the drawer if the cart did change.
   */
  async explainFailure(lead, before) {
    const cart = await this.fetchCart();
    if (cart && !Number.isNaN(before) && cart.item_count !== before) {
      this.dispatchEvent(new CartAddEvent({ items: [] }, this.id, { source: this.tagName.toLowerCase(), itemCount: cart.item_count }));
      this.error(`${lead} Part of it did reach your cart — please check the cart before trying again.`);
    } else if (cart && !Number.isNaN(before)) {
      this.error(`${lead} Nothing was added.`);
    } else {
      this.error(`${lead} Please check your cart before trying again.`);
    }
  }

  /** Adds one or more lines. @param {{id:number, quantity:number}[]} items @param {HTMLElement} [button] */
  async addLines(items, button) {
    if (this.busy) return;
    this.busy = true;
    this.error('');
    if (button) button.dataset.state = 'loading';
    this.render();
    // Count from the live cart (the header badge is not rendered while the cart is empty).
    const before = (await this.fetchCart())?.item_count ?? this.badgeCount();
    try {
      const res = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status) {
        await this.explainFailure(`Couldn't add this to your cart: ${data.description || data.message || 'please try again'}.`, before);
        return;
      }
      const cart = await this.fetchCart();
      this.dispatchEvent(new CartAddEvent(data, this.id, {
        source: this.tagName.toLowerCase(),
        productId: this.dataset.productId,
        variantId: String(items[0]?.id ?? ''),
        itemCount: cart?.item_count,
      }));
    } catch (err) {
      console.error('[aromely-kit-mvp] add to cart failed', err);
      await this.explainFailure('We couldn\'t confirm this with the store — check your connection.', before);
    } finally {
      this.busy = false;
      if (button) button.dataset.state = '';
      this.render();
    }
  }

  addToCart() {
    const id = Number(this.dataset.variantId);
    if (!id) return;
    this.addLines([{ id, quantity: this.qty }], this.refs.addBtn);
  }

  /** Quick-add from a row (other samples, full-size bottle). */
  quickAdd(event) {
    const button = event.target.closest('[data-variant-id]');
    if (!button || button.disabled) return;
    this.addLines([{ id: Number(button.dataset.variantId), quantity: 1 }], button);
  }

  error(text) {
    const el = this.refs.error;
    if (!el) return;
    el.hidden = !text;
    el.textContent = text || '';
  }

  /* ─── sticky bar (mobile) ─── */
  setupSticky(section) {
    this.#sticky = section?.querySelector('[data-akm-sticky]') || null;
    if (!this.#sticky || !this.refs.addBtn) return;
    this.#sticky.querySelector('[data-akm-sticky-btn]')?.addEventListener('click', () => this.addToCart(), { signal: this.#ac.signal });
    let atcVisible = true;
    let drawerOpen = false;
    const update = () => this.setStickyVisible(!atcVisible && !drawerOpen && window.innerWidth < 1024);
    const measure = () => { atcVisible = this.refs.addBtn.getBoundingClientRect().bottom > 0; };
    this.#stickyIO = new IntersectionObserver(() => { measure(); update(); }, { threshold: 0 });
    this.#stickyIO.observe(this.refs.addBtn);
    window.addEventListener('scroll', () => { measure(); update(); }, { passive: true, signal: this.#ac.signal });
    const dialog = document.querySelector('aromely-cart-drawer-component dialog, cart-drawer-component dialog');
    if (dialog) {
      drawerOpen = dialog.open;
      this.#drawerMO = new MutationObserver(() => { drawerOpen = dialog.open; update(); });
      this.#drawerMO.observe(dialog, { attributes: true, attributeFilter: ['open'] });
    }
    window.addEventListener('resize', update, { signal: this.#ac.signal });
  }

  setStickyVisible(show) {
    if (!this.#sticky) return;
    this.#sticky.hidden = !show;
    const root = this.closest('[data-akm-root]');
    if (root) root.style.setProperty('--sticky-h', show ? `${this.#sticky.offsetHeight}px` : '0px');
  }

  renderCommon() {
    const r = this.refs;
    // October Sale: the add button and sticky bar show the sale total while the promotion is live.
    const total = this.saleCents(Number(this.dataset.priceCents) || 0) * this.qty;
    const available = this.dataset.variantAvailable !== 'false';
    if (r.qtyValue) r.qtyValue.value = String(this.qty);
    if (r.qtyMinus) r.qtyMinus.disabled = this.qty <= 1;
    if (r.qtyPlus) r.qtyPlus.disabled = this.qty >= 10;
    if (r.addBtn) {
      r.addBtn.disabled = !available || this.busy;
      if (r.addLabel) r.addLabel.textContent = !available ? 'Sold out' : `Add to cart · ${this.money(total)}`;
    }
    if (this.#sticky) {
      const price = this.#sticky.querySelector('[data-akm-sticky-price]');
      const btn = this.#sticky.querySelector('[data-akm-sticky-btn]');
      if (price) price.textContent = this.money(total);
      if (btn) { btn.textContent = available ? 'Add to cart' : 'Sold out'; btn.disabled = !available || this.busy; }
    }
  }

  render() { this.renderCommon(); }
}

/**
 * <aromely-sample-mvp> — 20 ml sample page. Adds the sample; recommends the full-size bottle
 * for the diffuser the shopper picks (sizes come from the diffuser's fit profile).
 */
class AromelySampleMvp extends AromelyKitBase {
  #diffuser = null;

  /** Diffuser tile click: show the recommended and larger bottle for that model. */
  pickDiffuser(event) {
    const tile = event.target.closest('[data-diffuser]');
    if (!tile) return;
    this.#diffuser = tile.dataset;
    for (const t of this.refs.diffTiles || []) t.setAttribute('aria-pressed', String(t === tile));
    this.#renderFit();
  }

  #renderFit() {
    const r = this.refs;
    const d = this.#diffuser;
    if (!r.fitOut) return;
    if (!d) { r.fitOut.hidden = true; return; }
    const sizes = JSON.parse(this.dataset.fullSizes || '[]'); // [{ml, label, price, id, available}]
    const byMl = (ml) => sizes.find((s) => Number(s.ml) === Number(ml));
    const rec = byMl(d.favoriteMl);
    const start = byMl(d.starterMl);
    r.fitOut.hidden = false;
    if (r.fitFor) r.fitFor.textContent = `For the ${d.name} — ${d.tankMl} ml bottle inside`;
    const row = (s, kicker, why) => s ? `
      <div class="afp-lrow${kicker === 'Recommended' ? ' afp-lrow--rec' : ''}">
        <span class="afp-lk">${kicker}</span>
        <span class="afp-lv">${s.label} <span>· ${this.money(s.price)}</span></span>
        <span class="afp-lw">${why}</span>
        <button type="button" class="afp-btn ${kicker === 'Recommended' ? 'afp-btn--primary' : 'afp-btn--ghost'} afp-btn--sm" data-variant-id="${s.id}" on:click="/quickAdd"${s.available ? '' : ' disabled'}>${s.available ? 'Add' : 'Sold out'}</button>
      </div>` : '';
    r.fitLadder.innerHTML =
      row(rec, 'Recommended', `The bottle ${d.name} owners come back for — on average it lasts 1 to 3 months, depending on use and the size of the area.`) +
      (start && start.ml !== rec?.ml ? row(start, 'Trying it first', `A smaller fill of the same oil if you want to live with it a little longer before the ${rec ? rec.label : 'larger size'}.`) : '');
  }
}

/**
 * <aromely-bundle-mvp> — gift-set page. One real variant (the kit), optional colour choice
 * that also filters the gallery, and a live "bought separately / you save" line.
 */
class AromelyBundleMvp extends AromelyKitBase {
  onColorChange(event) {
    const button = event.target.closest('[data-variant-id]');
    if (!button || button.getAttribute('aria-disabled') === 'true') return;
    this.dataset.variantId = button.dataset.variantId;
    this.dataset.variantTitle = button.dataset.variantTitle || '';
    this.dataset.variantAvailable = button.dataset.available || 'true';
    this.dataset.priceCents = button.dataset.priceCents || this.dataset.priceCents;
    for (const b of this.refs.colorBtns || []) b.setAttribute('aria-pressed', String(b === button));
    if (this.refs.colorName) this.refs.colorName.textContent = button.dataset.variantTitle || '';
    this.closest('[data-akm-root]')?.querySelector('aromely-diffuser-gallery')?.filter?.(button.dataset.color || '');
    // The diffuser thumbnail in "What's inside" follows the colour when a tagged photo exists.
    const c = (button.dataset.color || '').toLowerCase();
    const key = c ? `thumb${c[0].toUpperCase()}${c.slice(1)}` : '';
    for (const img of this.querySelectorAll('[data-thumb-swap]')) {
      if (key && img.dataset[key]) img.src = img.dataset[key];
    }
    this.render();
  }

  render() {
    this.renderCommon();
    const r = this.refs;
    const price = Number(this.dataset.priceCents) || 0;
    // October Sale: the kit line (refs kitPrice/kitCompare/kitBadge from aromely-price) repaints at the
    // sale price on every colour change, never back to the full price while the promotion is live.
    const salePrice = this.paintSaleLine(r.kitPrice, r.kitCompare, r.kitBadge, price);
    // "Bought separately" compares like with like: both sides at the sale price while the sale is on.
    const separate = this.saleCents(Number(this.dataset.separateCents) || 0);
    if (r.saveLine) {
      const save = separate - salePrice;
      r.saveLine.hidden = !(separate > 0 && save > 0);
      if (save > 0) r.saveLine.innerHTML = `Bought separately: <s>${this.money(separate)}</s> · <b>You save ${this.money(save)}</b>`;
    }
    if (r.heroPrice) r.heroPrice.textContent = this.money(salePrice);
  }
}

if (!customElements.get('aromely-sample-mvp')) customElements.define('aromely-sample-mvp', AromelySampleMvp);
if (!customElements.get('aromely-bundle-mvp')) customElements.define('aromely-bundle-mvp', AromelyBundleMvp);
